import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import type { FocusEvent, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useGridListbox } from "../../../hooks/useGridListbox";
import { usePopoverPosition } from "../../../hooks/usePopoverPosition";
import { ICON_SIZE, type InputSize, PAD_X, SIZE_CLASSES } from "../Input";
import { TRIGGER_BUTTON_CLASSES, TYPED_END_PAD } from "./pickerSizes";

export interface DatePickerProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: InputSize;
}

type DateView = "day" | "month" | "year";

const DAY_COLUMNS = 7;
const YEAR_PAGE_SIZE = 12;
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_SHORT_LABELS = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat(undefined, { month: "short" }).format(new Date(2000, i, 1)),
);
const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatMMDDYYYY(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${month}/${day}/${year}`;
}

function maskDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join("/");
}

function parseMMDDYYYY(text: string): string | null {
  const match = text.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return toIsoDate(date);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function getMonthGrid(monthStart: Date) {
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const todayIso = toIsoDate(new Date());

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(date.getDate() + i);
    return {
      value: toIsoDate(date),
      day: date.getDate(),
      inMonth: date.getMonth() === monthStart.getMonth(),
      isToday: toIsoDate(date) === todayIso,
    };
  });
}

function getYearPageStart(year: number): number {
  return Math.floor(year / YEAR_PAGE_SIZE) * YEAR_PAGE_SIZE;
}

export function DatePicker({
  label,
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  size = "lg",
}: Readonly<DatePickerProps>) {
  const [view, setView] = useState<DateView>("day");
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(value ? parseIsoDate(value) : new Date()));
  const [yearPageStart, setYearPageStart] = useState(() => getYearPageStart(viewMonth.getFullYear()));
  const [inputText, setInputText] = useState(() => (value ? formatMMDDYYYY(value) : ""));
  const monthStart = viewMonth;
  const options = getMonthGrid(monthStart);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pointerDownRef = useRef(false);
  const [focusMethod, setFocusMethod] = useState<"pointer" | "keyboard" | null>(null);
  const isKeyboardFocus = focusMethod === "keyboard";
  const isFocused = focusMethod !== null;

  const handleInputPointerDown = (_event: ReactPointerEvent<HTMLInputElement>) => {
    pointerDownRef.current = true;
  };

  const handleInputFocus = (_event: FocusEvent<HTMLInputElement>) => {
    setFocusMethod(pointerDownRef.current ? "pointer" : "keyboard");
    pointerDownRef.current = false;
  };

  const handleInputBlur = (_event: FocusEvent<HTMLInputElement>) => {
    setFocusMethod(null);
  };

  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    selectedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  } = useGridListbox({ id: label ?? "date-picker", options, value, onChange, columns: DAY_COLUMNS, disabled });

  const { panelRef, style: panelStyle } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef: fieldRef,
  });

  useEffect(() => {
    setInputText(value ? formatMMDDYYYY(value) : "");
  }, [value]);

  const commitTypedText = () => {
    const parsed = parseMMDDYYYY(inputText);
    if (parsed) {
      onChange(parsed);
      setInputText(formatMMDDYYYY(parsed));
    } else {
      setInputText(value ? formatMMDDYYYY(value) : "");
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately re-syncs only when the panel opens, not on every value change, so this never fights the user's own in-grid navigation.
  useEffect(() => {
    if (isOpen) {
      const nextMonthStart = startOfMonth(value ? parseIsoDate(value) : new Date());
      setViewMonth(nextMonthStart);
      setYearPageStart(getYearPageStart(nextMonthStart.getFullYear()));
      setView("day");
    }
  }, [isOpen]);

  const goToMonth = (delta: number) => {
    const nextMonthStart = addMonths(monthStart, delta);
    const nextOptions = getMonthGrid(nextMonthStart);
    const anchorIso = toIsoDate(nextMonthStart);
    setViewMonth(nextMonthStart);
    openAt(nextOptions.findIndex((option) => option.value === anchorIso));
  };

  const goToYear = (delta: number) => {
    setViewMonth(new Date(monthStart.getFullYear() + delta, monthStart.getMonth(), 1));
  };

  const goToYearPage = (delta: number) => {
    setYearPageStart((start) => start + delta * YEAR_PAGE_SIZE);
  };

  const headerLabel =
    view === "day"
      ? MONTH_LABEL_FORMATTER.format(monthStart)
      : view === "month"
        ? String(monthStart.getFullYear())
        : `${yearPageStart}–${yearPageStart + YEAR_PAGE_SIZE - 1}`;

  const handleHeaderPrev = () => (view === "day" ? goToMonth(-1) : view === "month" ? goToYear(-1) : goToYearPage(-1));
  const handleHeaderNext = () => (view === "day" ? goToMonth(1) : view === "month" ? goToYear(1) : goToYearPage(1));

  const handleHeaderLabelClick = () => {
    if (view === "day") setView("month");
    else if (view === "month") setView("year");
  };

  return (
    <div ref={containerRef} className="relative isolate">
      {label && <div className="mb-1.5 text-sm font-medium text-content-secondary">{label}</div>}
      <div
        ref={fieldRef}
        className={[
          "flex w-full items-center gap-1 border bg-surface-elevated transition-colors",
          SIZE_CLASSES[size],
          PAD_X[size].start,
          TYPED_END_PAD[size],
          isOpen || isFocused ? "border-border-focus" : "border-border-input",
          isKeyboardFocus ? "input-focus-ring" : "",
          disabled ? "cursor-not-allowed opacity-50" : "",
        ].join(" ")}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          maxLength={10}
          disabled={disabled}
          value={inputText}
          onChange={(e) => setInputText(maskDateInput(e.target.value))}
          onPointerDown={handleInputPointerDown}
          onFocus={handleInputFocus}
          onBlur={(e) => {
            handleInputBlur(e);
            commitTypedText();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitTypedText();
            } else if (e.key === "Escape") {
              setInputText(value ? formatMMDDYYYY(value) : "");
            }
          }}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-sm leading-none pb-0.5 text-content-primary outline-none placeholder:text-content-tertiary"
        />
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          role="combobox"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-label="Open calendar"
          onClick={() => (isOpen ? setIsOpen(false) : openAt(selectedIndex >= 0 ? selectedIndex : 0))}
          onKeyDown={handleTriggerKeyDown}
          className={`btn-focus-ring grid ${TRIGGER_BUTTON_CLASSES[size]} shrink-0 cursor-pointer place-items-center rounded-md text-content-tertiary hover:bg-surface-base`}
        >
          <Calendar size={ICON_SIZE[size]} />
        </button>
      </div>
      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          id={listboxId}
          aria-label={label ?? "Date"}
          style={panelStyle}
          className="z-[110] w-64 rounded-lg border border-border-input bg-surface-elevated p-3 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={handleHeaderPrev}
              aria-label="Previous"
              className="btn-focus-ring grid size-6 cursor-pointer place-items-center rounded-md text-content-secondary hover:bg-surface-base"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={handleHeaderLabelClick}
              disabled={view === "year"}
              className="btn-focus-ring cursor-pointer rounded-md px-2 py-0.5 text-sm font-medium text-content-primary hover:bg-surface-base disabled:cursor-default disabled:hover:bg-transparent"
            >
              {headerLabel}
            </button>
            <button
              type="button"
              onClick={handleHeaderNext}
              aria-label="Next"
              className="btn-focus-ring grid size-6 cursor-pointer place-items-center rounded-md text-content-secondary hover:bg-surface-base"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {view === "day" && (
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAY_LABELS.map((weekday) => (
                <div key={weekday} className="grid h-7 place-items-center text-xs font-medium text-content-tertiary">
                  {weekday}
                </div>
              ))}
              {options.map((option, index) => (
                <button
                  key={option.value}
                  id={optionId(index)}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  type="button"
                  role="option"
                  aria-selected={index === selectedIndex}
                  onMouseEnter={() => openAt(index)}
                  onClick={() => commitSelection(index)}
                  className={[
                    "grid size-7 cursor-pointer place-items-center rounded-md text-sm transition-colors",
                    option.inMonth ? "text-content-primary" : "text-content-quaternary",
                    index === selectedIndex
                      ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                      : index === highlightedIndex
                        ? "bg-surface-base"
                        : "hover:bg-surface-base",
                    option.isToday && index !== selectedIndex ? "font-semibold" : "",
                  ].join(" ")}
                >
                  {option.day}
                </button>
              ))}
            </div>
          )}

          {view === "month" && (
            <div className="grid grid-cols-4 gap-1">
              {MONTH_SHORT_LABELS.map((monthLabel, monthIndex) => (
                <button
                  key={monthLabel}
                  type="button"
                  onClick={() => {
                    setViewMonth(new Date(monthStart.getFullYear(), monthIndex, 1));
                    setView("day");
                  }}
                  className={[
                    "cursor-pointer rounded-md py-2 text-sm transition-colors",
                    monthIndex === monthStart.getMonth()
                      ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                      : "text-content-primary hover:bg-surface-base",
                  ].join(" ")}
                >
                  {monthLabel}
                </button>
              ))}
            </div>
          )}

          {view === "year" && (
            <div className="grid grid-cols-4 gap-1">
              {Array.from({ length: YEAR_PAGE_SIZE }, (_, i) => yearPageStart + i).map((year) => (
                <button
                  key={year}
                  type="button"
                  onClick={() => {
                    setViewMonth(new Date(year, monthStart.getMonth(), 1));
                    setView("month");
                  }}
                  className={[
                    "cursor-pointer rounded-md py-2 text-sm transition-colors",
                    year === monthStart.getFullYear()
                      ? "bg-brand-primary-subtle text-brand-primary-on-subtle"
                      : "text-content-primary hover:bg-surface-base",
                  ].join(" ")}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
